import { getSelectorAttribute, perUnitUsageChargeFormSelectors } from '@weft/app-selectors';
import type { ObjectId, UsageProductVariantInput } from '@weft/types';
import type { Nullable } from '@weft/utils';
import { isNotNullOrUndefined, withDefault } from '@weft/utils';
import { get } from 'lodash';
import React, { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';
import { Input } from '../../../../../../../../components/design/Input';
import { useFormErrorNotificationContext } from '../../../../../../../../contexts/form-error-notification-context/form-error-notification-context';
import { useMeters } from '../../../../../../../../hooks/requests/use-meters';
import type { NewPlanForm } from '../../../../CreatePlanForm/create-plan-form-types/new-plan-form';
import type { PlanInputForm } from '../../../../CreatePlanForm/create-plan-form-types/plan-input-form';
import type { CreatePlanTab } from '../../../../CreatePlanPageTabs/create-plan-page-types/create-plan-tab';
import { CREATE_PLAN_TABS } from '../../../../CreatePlanPageTabs/create-plan-page-types/create-plan-tab';
import { ProductSourceSelect } from '../../ProductSourceSelect/ProductSourceSelect';

type PerUnitUsageChargeEditProps = {
  productIndex: number;
  productVariant: UsageProductVariantInput;
  productVariantIndex: number;
};

type SelectedProductNameType = {
  isPreviousMeterName: boolean;
  value: string;
};

export const PerUnitUsageChargeForm: React.FC<PerUnitUsageChargeEditProps> = ({ productIndex, productVariantIndex }) => {
  const { registerErrorField } = useFormErrorNotificationContext<PlanInputForm, CreatePlanTab>();
  const { register, control, getFieldState, formState: { errors }, setValue } = useFormContext<PlanInputForm, NewPlanForm>();
  const [selectedProductName, setSelectedProductName] = useState<SelectedProductNameType>({ isPreviousMeterName: false, value: '' });
  const { data: { meters } } = useMeters();

  const nameRegister = register(`usageProducts.${productIndex}.variants.${productVariantIndex}.name`);
  const pricePerUnitRegister = register(`usageProducts.${productIndex}.variants.${productVariantIndex}.pricing.priceConfiguration.price`, {
    valueAsNumber: true,
  });

  registerErrorField(nameRegister.name, CREATE_PLAN_TABS.tierAndVolumePricing);
  registerErrorField(pricePerUnitRegister.name, CREATE_PLAN_TABS.tierAndVolumePricing);
  registerErrorField(`usageProducts.${productIndex}.variants.${productVariantIndex}.meterId`, CREATE_PLAN_TABS.tierAndVolumePricing);

  function handleMeterSelect(newMeterId: Nullable<ObjectId>) {
    if (selectedProductName.isPreviousMeterName || selectedProductName.value === '') {
      const meterName = withDefault(meters.find(({ id }) => id === newMeterId)?.name, '');
      setSelectedProductName({ isPreviousMeterName: true, value: meterName });
      setValue(`usageProducts.${productIndex}.variants.${productVariantIndex}.name`, meterName);
    }
  }

  function handleNameChange(value: string) {
    setSelectedProductName({ isPreviousMeterName: false, value });
    setValue(nameRegister.name, value);
  }

  return (
    <div className='mb-5 flex flex-wrap justify-between gap-4'>
      <div className="flex max-w-[33%] flex-1 flex-col truncate pt-3">
        <Controller
          control={control}
          name={`usageProducts.${productIndex}.variants.${productVariantIndex}.meterId`}
          render={({ field }) => (
            <ProductSourceSelect
              error={isNotNullOrUndefined(get(errors, `usageProducts.${productIndex}.variants.${productVariantIndex}.meterId`))}
              helperText={getFieldState(`usageProducts.${productIndex}.variants.${productVariantIndex}.meterId`).error?.message}
              value={field.value}
              onChange={(event, newValue) => {
                event.preventDefault();
                handleMeterSelect(newValue);
                field.onChange(newValue);
              }}
            />
          )}
        />
      </div>

      <div className="flex min-w-[120px] flex-1">
        <Input
          {...getSelectorAttribute(perUnitUsageChargeFormSelectors.nameInput)}
          label='Product name'
          {...nameRegister}
          onChange={(event) => handleNameChange(event.currentTarget.value)}
          errorMessage={get(errors, nameRegister.name.toString())?.message}
          className={twMerge('max-w-[100%] text-base placeholder:font-normal', getFieldState(`usageProducts.${productIndex}.variants.${productVariantIndex}.name`).invalid ? 'border border-input-error' : '')}
          labelProps={{
            className: 'mb-2 text-sm text-label whitespace-nowrap',
          }}
          debounce={false}
        />
      </div>
      <div className="flex min-w-[120px] flex-1">
        <Input
          {...getSelectorAttribute(perUnitUsageChargeFormSelectors.priceInput)}
          label='Price'
          prefix='$'
          {...pricePerUnitRegister}
          type="number"
          step="0.01"
          debounce={false}
          className={twMerge('max-w-[100%] text-base placeholder:font-normal', getFieldState(`usageProducts.${productIndex}.variants.${productVariantIndex}.name`).invalid ? 'border border-input-error' : '')}
          errorMessage={get(errors, pricePerUnitRegister.name.toString())?.message}
        />
      </div>
    </div>
  );
};
